(() => {
  'use strict';

  const root = document.documentElement;

  // Phones/tablets: hold the page still while the cart drawer is open. iOS Safari lets touch
  // scrolling move a page whose body is overflow:hidden (and Safari 26.0 ignores it), so the
  // page is pinned with position:fixed and put back exactly where it was when the cart closes.
  const lockQuery = window.matchMedia('(max-width: 1023px)');
  let lockedY = null;
  const lockPage = (open) => {
    const style = document.body.style;
    if (open && lockedY === null && lockQuery.matches) {
      lockedY = window.scrollY || window.pageYOffset || 0;
      style.position = 'fixed';
      style.top = `-${lockedY}px`;
      style.left = '0';
      style.right = '0';
      style.width = '100%';
    } else if (!open && lockedY !== null) {
      const y = lockedY;
      lockedY = null;
      style.position = '';
      style.top = '';
      style.left = '';
      style.right = '';
      style.width = '';
      const previous = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      window.scrollTo(0, y);
      root.style.scrollBehavior = previous;
    }
  };

  document.addEventListener('ps:cart-visibility', (event) => {
    const open = Boolean(event.detail?.open);
    root.classList.toggle('ps-cart-is-open', open);
    lockPage(open);
  });

  window.addEventListener('pagehide', () => {
    root.classList.remove('ps-cart-is-open');
    lockPage(false);
  });

  /*
    Shopify splits one variant into two cart lines when the automatic
    "Buy 1, get 1 $39.95 off" discount applies (2-pack). The Liquid in
    snippets/ps-cart-trust.liquid emits those groups as JSON; here we show each
    group as ONE row with ONE quantity, and route quantity changes through
    /cart/update.js so both lines move together.
  */
  const CART_ROOTS = '#update-cart, .ps-cart-page-native';
  const escapeKey = (key) => (window.CSS && CSS.escape ? CSS.escape(key) : key.replace(/([^\w-])/g, '\\$1'));
  const priceHtml = (group) =>
    `<s class="cap mr-1 rtl:ml-1 rtl:mr-0 inline-block opacity-70">${group.original}</s>` +
    `<span class="selection:bg-text-[rgb(var(--colors-price-sale),0.2)] price-sale">${group.final}</span>`;

  const setHtml = (element, html) => {
    if (!element || element.dataset.psPriced === html) return;
    element.innerHTML = html;
    element.dataset.psPriced = html;
  };

  const mergeGroup = (scope, group) => {
    if (!group || !Array.isArray(group.keys) || group.keys.length < 2) return;
    const rows = group.keys
      .map((key) => scope.querySelector(`#cart-qty-${escapeKey(key)}`))
      .map((input) => (input ? input.closest('.cart-item') : null))
      .filter(Boolean);
    if (rows.length < 2) return;

    const [primary, ...rest] = rows;
    rest.forEach((row) => {
      if (row.dataset.psMerged !== 'hidden') {
        row.dataset.psMerged = 'hidden';
        row.style.display = 'none';
        row.setAttribute('aria-hidden', 'true');
      }
    });

    const keys = group.keys.join(',');
    if (primary.dataset.psGroup !== keys) primary.dataset.psGroup = keys;
    if (primary.dataset.psGroupQty !== String(group.qty)) primary.dataset.psGroupQty = String(group.qty);

    const input = primary.querySelector(`#cart-qty-${escapeKey(group.keys[0])}`);
    if (input && input.value !== String(group.qty)) input.value = String(group.qty);

    const total = priceHtml(group);
    const unit = `<span>${group.unit}</span>`;
    primary.querySelectorAll('.price').forEach((price) => {
      if (price.closest('.ps-cart-trust, #mini-cart-upsell, .product-recommendations')) return;
      if (price.parentElement && price.parentElement.closest('[data-ps-priced]')) return;
      const isDrawer = price.classList.contains('ps-cart__item-price');
      const isLineTotal = isDrawer || /(^|\s)cart-item-price-/.test(price.className) || Boolean(price.closest('td.md\\:hidden'));
      setHtml(price, isLineTotal ? total : unit);
    });
  };

  const mergeScope = (scope) => {
    const data = scope.querySelector('script[data-ps-cart-groups]');
    if (!data) return;
    let groups;
    try {
      groups = JSON.parse(data.textContent || '[]');
    } catch (error) {
      return;
    }
    if (!Array.isArray(groups)) return;
    groups.forEach((group) => mergeGroup(scope, group));
  };

  const mergeAll = () => {
    document.querySelectorAll(CART_ROOTS).forEach(mergeScope);
  };

  window.psCartGroup = async (keys, qty) => {
    const list = String(keys || '').split(',').filter(Boolean);
    if (!list.length) return;
    qty = Math.max(0, parseInt(qty, 10) || 0);
    const helper = window.Alpine ? Alpine.store('xCartHelper') : null;
    const first = list[0];
    const removeEl = document.getElementById(`remove-${first}`);
    const loadingEl = document.getElementById(`loading-${first}`);
    if (removeEl) removeEl.style.display = 'none';
    if (loadingEl) loadingEl.classList.remove('hidden');
    const updates = {};
    list.forEach((key, index) => { updates[key] = index === 0 ? qty : 0; });
    try {
      if (helper && helper.waitForCartUpdate) await helper.waitForCartUpdate();
      window.updatingCart = true;
      const sectionIds = helper && helper.getSectionsToRender ? helper.getSectionsToRender().map((section) => section.id) : [];
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const state = await response.json();
      if (!response.ok || state.status == 422 || state.errors) {
        if (window.Alpine && Alpine.store('xMiniCart') && Alpine.store('xMiniCart').reLoad) Alpine.store('xMiniCart').reLoad();
        else window.location.reload();
        return;
      }
      // /cart/update.js does not return rendered sections; fetch them from the Section Rendering API.
      let sections = null;
      if (helper && helper.reRenderSections && sectionIds.length) {
        // The Section Rendering API accepts at most 5 sections per request.
        const batches = [];
        for (let i = 0; i < sectionIds.length; i += 5) batches.push(sectionIds.slice(i, i + 5));
        const results = await Promise.all(batches.map((ids) =>
          fetch(`${window.location.pathname}?sections=${ids.join(',')}`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        ));
        sections = results.every(Boolean) ? Object.assign({}, ...results) : null;
        if (sections && !sectionIds.some((id) => typeof sections[id] === 'string')) sections = null;
      }
      if (sections) {
        helper.reRenderSections(sections);
        helper.currentItemCount = state.item_count;
        mergeAll();
      } else if (window.Alpine && Alpine.store('xMiniCart') && Alpine.store('xMiniCart').reLoad) {
        Alpine.store('xMiniCart').reLoad();
      } else {
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
    } catch (error) {
      console.error('psCartGroup', error);
      if (loadingEl) loadingEl.classList.add('hidden');
      if (removeEl) removeEl.style.display = '';
    } finally {
      window.updatingCart = false;
    }
  };

  // Route +/-/remove/typed quantity on a merged row through psCartGroup (capture phase
  // runs before the theme's own Alpine handlers on the buttons).
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button) return;
    const row = button.closest('[data-ps-group]');
    if (!row) return;
    const label = button.getAttribute('aria-label') || '';
    const qty = parseInt(row.dataset.psGroupQty, 10) || 0;
    let next = null;
    if (label === 'button-minus') next = qty - 1;
    else if (label === 'button-plus') next = qty + 1;
    else if (label === 'button-remove') next = 0;
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    window.psCartGroup(row.dataset.psGroup, next);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !/^cart-qty-/.test(input.id)) return;
    const row = input.closest('[data-ps-group]');
    if (!row) return;
    event.stopPropagation();
    event.stopImmediatePropagation();
    window.psCartGroup(row.dataset.psGroup, input.value);
  }, true);

  let scheduled = false;
  const scheduleMerge = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      mergeAll();
    });
  };

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      if (target && target.closest(CART_ROOTS)) {
        scheduleMerge();
        return;
      }
    }
  });

  const start = () => {
    mergeAll();
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('eurus:cart:items-changed', scheduleMerge);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
